/**
 * "Stop the campaign for leads who are already connected" on a "Send LinkedIn Invite" step
 * (PREC-4426): one decision, read by the scheduler (send time), the campaign builder (the toggle and
 * a new step's default), the MCP campaign tools (`campaign-sequence-graph.ts`) and the assign
 * pickers' warning.
 *
 * Off is today's behaviour: the invite is skipped, the timeline reads "Already connected —
 * invitation skipped" and the next step runs. On, the step fails for that lead with
 * `ALREADY_CONNECTED`, the lead's campaign ends, and the lead shows in Outbox → Error.
 *
 * A NEW invite step is created with the switch on. A stored step without the flag stays off: it was
 * built before the switch existed, and waking a setting up changes nothing for a company that never
 * set it (root CLAUDE.md). So the flag is read as `=== true`, never as "absent means on".
 */

/** The key on the invite node's `data` in `list.campaignConfig`. */
export const STOP_IF_ALREADY_CONNECTED_KEY = "stopIfAlreadyConnected";

const INVITE_NODE_TYPE = "linkedinInviteNode";

/** What a new invite step is created with, in the builder and over MCP. */
export const NEW_INVITE_STEP_DATA: Readonly<{ stopIfAlreadyConnected: true }> = {
	stopIfAlreadyConnected: true,
};

/** Whether this invite step stops a lead its sender is already connected to. */
export const inviteStopsForAlreadyConnected = (data: object | null | undefined): boolean =>
	(data as { stopIfAlreadyConnected?: unknown } | null | undefined)?.stopIfAlreadyConnected === true;

/** The data a new node of `type` starts with on top of the builder's own fields. */
export const newStepDataFor = (type: string): Partial<typeof NEW_INVITE_STEP_DATA> =>
	type === INVITE_NODE_TYPE ? { ...NEW_INVITE_STEP_DATA } : {};

export interface CampaignInviteStopMode {
	/** The campaign has a "Send LinkedIn Invite" step, so a sender's connection matters. */
	hasInviteStep: boolean;
	/** One of its invite steps stops for an already connected lead. */
	stops: boolean;
}

/** Whether a campaign's steps invite on LinkedIn, and whether they stop for a connected lead. */
export function campaignInviteStopMode(
	nodes: ReadonlyArray<{ type?: string; data?: object }> | null | undefined
): CampaignInviteStopMode {
	const invites = (nodes ?? []).filter((node) => node.type === INVITE_NODE_TYPE);

	return {
		hasInviteStep: invites.length > 0,
		stops: invites.some((node) => inviteStopsForAlreadyConnected(node.data)),
	};
}
